import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { BrandMark, Wordmark } from "@/components/brand/Brand";
import { CloseButton, IconButton, SideDialog } from "@/components/ui/Drawer";
import { Menu } from "@/components/ui/Menu";
import { BRAND } from "@/lib/brand";
import { hasFeature } from "@/lib/license";
import { useAuth } from "@/store/auth";

/**
 * 앱 셸. 넓은 화면은 전체 사이드바, 좁은 데스크톱은 아이콘 메뉴, 모바일은 상단 메뉴 버튼을 쓴다.
 *
 * 메뉴는 같은 위상의 것만 선다 , 산정 시작, 내 시나리오, 참고 데이터, 관리자 콘솔.
 * 예시(템플릿)는 메뉴가 아니라 '새 시나리오' 만들기 흐름 안에 있다. 계정,로그아웃은 맨 아래 이름 메뉴.
 */
function Icon({ name }: { name: "start" | "scenario" | "data" | "admin" | "menu" | "close" }) {
  switch (name) {
    case "start":
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
          <path d="M4 10h9M10 6l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 4v2M4 14v2" strokeLinecap="round" />
        </svg>
      );
    case "scenario":
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
          <rect x="3" y="4" width="14" height="12" rx="2" />
          <path d="M6 8h8M6 11h5" strokeLinecap="round" />
        </svg>
      );
    case "data":
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
          <ellipse cx="10" cy="5" rx="6" ry="2.2" />
          <path d="M4 5v10c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V5M4 10c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2" />
        </svg>
      );
    case "admin":
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden>
          <path d="M10 3l6 2.5v4.5c0 3.5-2.6 6-6 7-3.4-1-6-3.5-6-7V5.5L10 3z" strokeLinejoin="round" />
        </svg>
      );
    case "menu":
      return (
        <svg viewBox="0 0 20 20" className="size-icon-nav" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
          <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 20 20" className="size-icon" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
        </svg>
      );
  }
}

/** 사이드바와 서랍이 같은 내용을 쓴다 */
function SidebarContent({ onNavigate, controls }: { onNavigate?: () => void; controls?: ReactNode }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const item = ({ isActive }: { isActive: boolean }) => `sidebar-item${isActive ? " sidebar-item-on" : ""}`;
  return (
    <>
      <div className="sidebar-head">
        <NavLink to="/app" className="sidebar-brand" aria-label="산정 시작으로" onClick={onNavigate}>
          <BrandMark />
          <span className="sidebar-label text-subhead font-extrabold tracking-tight text-ink">{BRAND.NAME}</span>
        </NavLink>
        {controls}
      </div>
      <nav className="sidebar-nav" aria-label="주요 화면">
        <NavLink to="/app" end className={item} onClick={onNavigate} aria-label="산정 시작" title="산정 시작">
          <Icon name="start" />
          <span className="sidebar-label">산정 시작</span>
        </NavLink>
        <NavLink to="/app/scenarios" className={item} onClick={onNavigate} aria-label="내 시나리오" title="내 시나리오">
          <Icon name="scenario" />
          <span className="sidebar-label">내 시나리오</span>
        </NavLink>
        {hasFeature("execution_history", user?.license) && <NavLink to="/workspace" className={item} onClick={onNavigate} aria-label="회사 작업공간" title="회사 작업공간"><Icon name="scenario" /><span className="sidebar-label">회사 작업공간</span></NavLink>}
        <NavLink to="/data" className={item} onClick={onNavigate} aria-label="참고 데이터" title="참고 데이터">
          <Icon name="data" />
          <span className="sidebar-label">참고 데이터</span>
        </NavLink>
        {user?.role === "admin" && (
          <>
            <p className="sidebar-group">관리</p>
            <NavLink to="/admin" className={item} onClick={onNavigate} aria-label="관리자 콘솔" title="관리자 콘솔">
              <Icon name="admin" />
              <span className="sidebar-label">관리자 콘솔</span>
            </NavLink>
          </>
        )}
      </nav>
      {user && (
        <div className="sidebar-foot">
          <Menu
            label="계정 메뉴"
            align="start"
            placement="up"
            triggerClassName="sidebar-item w-full"
            trigger={
              <>
                <span className="sidebar-avatar" aria-hidden>{user.name.slice(0, 1)}</span>
                <span className="sidebar-label min-w-0 flex-1 text-left">
                  <span className="block truncate">{user.name}</span>
                  <span className="block truncate text-footnote font-normal text-ink-subtle">{user.organization}</span>
                </span>
              </>
            }
            head={<span className="font-mono">{user.email}</span>}
            items={[
              { label: "내 계정", to: "/account", hint: "프로필, 연락처, 비밀번호, 안내 수신" },
              {
                label: "로그아웃",
                danger: true,
                onSelect: () => {
                  void logout().then(() => nav("/login"));
                },
              },
            ]}
          />
        </div>
      )}
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("h2lca.sidebar.collapsed.v1") === "true"; }
    catch { return false; }
  });
  const loc = useLocation();
  // 화면이 바뀌면 서랍은 닫힌다
  useEffect(() => setOpen(false), [loc.pathname]);
  const collapse = (value: boolean) => {
    setCollapsed(value);
    try { localStorage.setItem("h2lca.sidebar.collapsed.v1", String(value)); } catch { /* 저장 불가여도 현재 화면에서는 동작한다. */ }
  };
  return (
    <div className="app-frame">
      <aside className="sidebar" aria-label="사이드바" data-collapsed={collapsed}>
        <SidebarContent controls={<>
          <IconButton label={collapsed ? "사이드바 펼치기" : "사이드바 접기"} className="sidebar-toggle sidebar-wide-toggle" aria-expanded={!collapsed} onClick={() => collapse(!collapsed)}><Icon name="menu" /></IconButton>
          <IconButton label="사이드바 펼치기" className="sidebar-toggle sidebar-expand" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen(true)}><Icon name="menu" /></IconButton>
        </>} />
      </aside>
      <header className="topbar">
        <IconButton label={open ? "메뉴 닫기" : "메뉴 열기"} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((v) => !v)}>
          <Icon name={open ? "close" : "menu"} />
        </IconButton>
        <NavLink to="/app" className="inline-flex" aria-label="산정 시작으로">
          <Wordmark />
        </NavLink>
      </header>
      {open && (
        <SideDialog label="전체 메뉴" onClose={() => setOpen(false)}>
          <SidebarContent onNavigate={() => setOpen(false)} controls={<CloseButton onClose={() => setOpen(false)} className="sidebar-toggle" />} />
        </SideDialog>
      )}
      <div className="app-main">{children}</div>
    </div>
  );
}
